import { useCallback, useState } from "react";
import { CompleteJson } from "../../../types/DealTracker";
import {
  useAddContainerTrackingMutation,
  useGetContainerStatusQuery,
} from "../../../store/apiSlices/containerTracking";
import { CrossIcon, SpinnerIcon } from "../../Icons";
import CustomModal from "../CustomModal";

type ValidationResult =
  | { error: string }
  | {
      logisticsProvider: string;
      logisticsCarrierCode: string;
      containerTracking: string;
    };

export default function ContainerStatusInfo({
  rowData,
}: {
  rowData: CompleteJson;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: containerStatusData, isLoading: isContainerStatusDataLoading } =
    useGetContainerStatusQuery(
      { row_id: rowData.row_id! },
      {
        skip: !rowData.row_id,
        refetchOnMountOrArgChange: true,
      },
    );
  const [addContainerTracking, { isLoading: addTrackingLoading }] =
    useAddContainerTrackingMutation();

  const validateLogistics = useCallback(
    (row: CompleteJson): ValidationResult => {
      const logisticsProvider = row.logistics_provider?.column_value?.trim();
      const containerTracking = row.container_tracking?.column_value?.trim();

      if (!logisticsProvider && !containerTracking) {
        return {
          error: "Logistics Provider and Container Tracking are required.",
        };
      }
      if (!logisticsProvider) {
        return { error: "Logistics Provider is required." };
      }
      if (!containerTracking) {
        return { error: "Container Tracking is required." };
      }

      const logisticsCarrierCode =
        logisticsProvider.match(/\(([^)]+)\)/)?.[1] ?? "";

      return {
        logisticsCarrierCode,
        logisticsProvider,
        containerTracking,
      };
    },
    [rowData],
  );

  const result = validateLogistics(rowData);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const getStatusClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-50 text-green-600 border border-green-600";
      case "completed":
        return "bg-green-50 text-green-600 border border-green-600";
      case "pending":
        return "bg-yellow-50 text-yellow-600 border border-yellow-600";
      case "failure":
        return "bg-red-50 text-red-600 border border-red-600";
      default:
        return "bg-gray-50 text-gray-500 border border-gray-400";
    }
  };

  const handleCheckStatus = async () => {
    if ("error" in result) return;

    const payload = {
      row_id: rowData.row_id,
      carrier_code: result.logisticsCarrierCode || result.logisticsProvider,
      tracking_number: result.containerTracking,
      mode: "ocean",
    };

    await addContainerTracking(payload);
  };

  if (isContainerStatusDataLoading)
    return (
      <div className="animate-pulse h-6 w-[50%] bg-slate-300 rounded-md" />
    );

  return (
    <div>
      {"error" in result ? (
        <p className="text-xs text-gray-400">{result.error}</p>
      ) : (
        <div className="text-xs flex gap-2 items-center">
          <p>
            <span className="font-semibold">Provider:</span>{" "}
            <i>{result.logisticsProvider}</i>
          </p>
          <p>
            <span className="font-semibold">Container Tracking:</span>{" "}
            <i>{result.containerTracking}</i>
          </p>

          {containerStatusData?.status === "fail" && (
            <button
              onClick={handleCheckStatus}
              className="bg-gray-200 px-2 py-[1px] text-custom-blue-medium rounded"
              disabled={addTrackingLoading}
            >
              {addTrackingLoading ? (
                <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
              ) : (
                "Check Live Status"
              )}
            </button>
          )}
        </div>
      )}

      {containerStatusData?.status === "success" && (
        <div className="mt-1 text-xs space-y-1">
          <p>
            <span className="font-medium">Status: </span>
            <span
              className={`text-[10px] px-2 rounded-xl ${getStatusClasses(
                containerStatusData?.data?.status,
              )}`}
            >
              {containerStatusData?.data?.status}
            </span>
            {containerStatusData?.data?.shipment_remark && (
              <span className="text-gray-500">
                {" "}
                - {containerStatusData?.data?.shipment_remark}
              </span>
            )}
          </p>
          {["active", "completed"].includes(
            containerStatusData?.data?.status.toLowerCase(),
          ) && (
            <button
              onClick={toggleModal}
              className="bg-gray-200 px-2 py-[1px] text-custom-blue-medium rounded hover:underline"
            >
              View details
            </button>
          )}
        </div>
      )}

      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-6xl h-full w-full"
      >
        <button onClick={toggleModal} className="float-end hover:scale-95">
          <CrossIcon className="size-6" />
        </button>

        <h2 className="text-gray-900 font-medium mb-4 space-x-1">
          <span>Tracking status for</span>
          {"logisticsProvider" in result && (
            <>
              <span className="font-semibold">Provider:</span>{" "}
              <i>{result.logisticsProvider}</i>
              <span className="font-semibold">Container Tracking:</span>{" "}
              <i>{result.containerTracking}</i>
            </>
          )}
        </h2>

        <iframe
          src={containerStatusData?.data?.tracking_link}
          width="100%"
          height="100%"
          style={{ border: 0 }}
        ></iframe>
      </CustomModal>
    </div>
  );
}
